import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
} from "@chakra-ui/form-control";
import {
  FormControlProps,
  StackProps,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Field, FieldProps } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { InfoIcon } from "../../../styles/icons";

export interface BaseFieldProps {
  name: string;
  label: string;
  helperText?: string;
  id?: string;
  infoText?: string;
  isRequired?: boolean;
  horizontal?: boolean;
  fcProps?: FormControlProps;
  valueProps?: StackProps;
}

export interface FieldWrapperProps extends BaseFieldProps {
  children: (props: FieldProps<any, any>) => JSX.Element;
  validate?: (value: any) => string | undefined;
}

const getNestedFormError = (errors: any, name: string) => {
  // Handle bracket notation like: extendedFields['field-id']
  // This matches the format used in the MemberForm for extended fields
  const bracketMatch = name.match(/^(.+)\['(.+)'\]$/);

  if (bracketMatch) {
    // Extract parent path and the key within brackets
    const [, parentPath, key] = bracketMatch;

    // Navigate to the parent object
    const parentParts = parentPath.split(".");
    let current = errors;

    for (const part of parentParts) {
      if (
        typeof current === "object" &&
        current &&
        current[part] !== undefined
      ) {
        current = current[part];
      } else {
        return undefined;
      }
    }

    // Access the final key
    if (typeof current === "object" && current && current[key]) {
      return current[key];
    }
    return undefined;
  }

  // Original logic for dot notation (e.g., "firstName", "homeContactDetails.EmailAddress")
  const nameParts = name.split(".");

  const error: string | undefined = nameParts.reduce((error, namePart) => {
    if (typeof error === "object" && error[namePart]) {
      return error[namePart];
    } else {
      if (typeof error === "string") {
        return error;
      } else {
        return undefined;
      }
    }
  }, errors);

  return error;
};

/** Provides label and error message rendering */
export const FieldWrapper = ({
  name,
  label,
  id: idProp,
  helperText,
  infoText,
  isRequired,
  horizontal,
  fcProps,
  valueProps,
  children,
  validate,
}: FieldWrapperProps) => {
  const id = idProp ?? `${name}-field`;
  return (
    <Field name={name} validate={validate}>
      {(fieldProps: FieldProps<any, any>) => (
        <FormControl
          isInvalid={!!getNestedFormError(fieldProps.form.errors, name)}
          isRequired={isRequired}
          display={horizontal ? "flex" : undefined}
          flexDir={horizontal ? "row" : undefined}
          {...fcProps}
        >
          <VStack
            w="100%"
            alignItems="flex-start"
            flex={horizontal ? 1 : undefined}
            pt={horizontal ? 2 : undefined}
          >
            {infoText && (
              <FormLabel htmlFor={id}>
                {label}{" "}
                <Tooltip label={infoText} placement="right">
                  <span>
                    <InfoIcon />
                  </span>
                </Tooltip>
              </FormLabel>
            )}
            {!infoText && <FormLabel htmlFor={id}>{label}</FormLabel>}
            {!!helperText && (
              <FormHelperText marginBottom="2">{helperText}</FormHelperText>
            )}
          </VStack>
          <VStack
            w="100%"
            alignItems="flex-start"
            alignSelf="center"
            flex={horizontal ? 3 : undefined}
            {...valueProps}
          >
            {children(fieldProps)}
            <FormErrorMessage>
              {getNestedFormError(fieldProps.form.errors, name)}
            </FormErrorMessage>
          </VStack>
        </FormControl>
      )}
    </Field>
  );
};
